import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  FolderEditPageData,
  FolderEditPageDataVariables,
  FolderType,
  UpdateFolder,
  UpdateFolderVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import { contentStatusOptions } from "common/content-status";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import {
  extractNullableMarkdownContent,
  markdownInput,
} from "common/input-types";
import { ADMIN_PREFIX, urls } from "common/urls";
import { adminFolderFragment } from "domain/admin/fragments";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

const GET_FOLDER = gql`
  query FolderEditPageData($folderId: ID!) {
    folderById(id: $folderId) {
      ...ContentBreadcrumbFragment
      ...AdminFolderFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${adminFolderFragment}
`;

const MUTATION = gql`
  mutation UpdateFolder($input: UpdateFolderDetailsInput!) {
    updateFolderDetails(input: $input) {
      folder {
        ...AdminFolderFragment
      }
    }
  }
  ${adminFolderFragment}
`;

interface FormValues {
  status: ContentStatus;
  title: string;
  description?: string;
}

type RouteProps = {
  folderId: string;
};

export const FolderEditPage: React.FC = () => {
  const { folderId } = useParams<RouteProps>();

  const methods = useForm<FormValues>();
  const [contentStatus, setContentStatus] = React.useState<ContentStatus>();

  const navigate = useNavigate();
  const previousPath = usePreviousPath();

  if (!folderId) {
    throw new Error("Unexpected error: Missing folderId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    FolderEditPageData,
    FolderEditPageDataVariables
  >(GET_FOLDER, {
    variables: {
      folderId,
    },
  });
  const [mutate, { error, loading }] = useMutation<
    UpdateFolder,
    UpdateFolderVariables
  >(MUTATION);

  useEffect(() => {
    if (data && data.folderById) {
      setContentStatus(data.folderById.status);
    }
  }, [data]);

  function exitEditPage() {
    if (previousPath && !previousPath.endsWith(`/${ADMIN_PREFIX}`)) {
      navigate(-1);
    } else {
      navigate(urls.admin);
    }
  }

  const onSubmit = async (content: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          id: folderId,
          status: { updatedValue: contentStatus },
          title: { updatedValue: content.title },
          description: {
            updatedValue:
              content.description == null
                ? null
                : markdownInput(content.description),
          },
        },
      },
    });

    if (result.data) {
      exitEditPage();
    }
  };

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.folderById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { title, context = [], description, type } = data.folderById;

  return (
    <AdministrationLayout title={title} context={context}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title={
              type === FolderType.GENERIC
                ? "Tittel på mappe"
                : "Tittel på selvtest"
            }
            fieldProps={methods.register("title", {
              required: "Du må fylle ut tittel",
            })}
            defaultValue={title}
          />

          {contentStatus && (
            <Select
              name="status"
              title="Status"
              options={contentStatusOptions()}
              value={contentStatus.toString()}
              onChange={(newValue: string) => {
                setContentStatus(newValue as ContentStatus);
              }}
            />
          )}

          <MarkdownField<FormValues>
            name="description"
            title="Beskrivelse"
            defaultValue={extractNullableMarkdownContent(description) ?? ""}
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit
            onCancel={exitEditPage}
            submitText="Lagre"
            isLoading={loading}
          />
        </form>
      </FormProvider>
    </AdministrationLayout>
  );
};
